import { Button } from 'base/ui/button';
import { ToastQueue } from 'base/ui/toast';
import {
  TextMedium,
  TextRegularLight,
  TextSmallLight,
  TextTiny,
  TextTinyLight,
  Title3,
} from 'base/ui/typography';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { aSectionElement } from '../add_panel/content';
import { EditorMode, editorModeStore } from '../editor_mode';
import { anInlineSection, aSection, Project } from '../model';
import { SidebarNavigation as WireframeNavigation } from '../sidebar/routes';
import DemoPage1Image from './demo-page-1.file.png';
import DemoPage2Image from './demo-page-2.file.png';
import DemoPageFacesImage from './demo-page-faces.file.png';
import styles from './demo_page.css';

const DEMO_SECTIONS = [
  aSection({
    name: 'Navbar',
    element: aSectionElement('navbar14_component'),
  }),
  aSection({
    name: 'Hero Header Section',
    description: 'Describe the product or service',
    element: aSectionElement('section_header2'),
  }),
  aSection({
    name: 'Logo Section',
    description: 'Show logos of key customers',
    element: aSectionElement('section_logo3'),
  }),
  aSection({
    name: 'How It Works Section',
    description: 'Describe how the product or service works',
    element: aSectionElement('section_layout396'),
  }),
  aSection({
    name: 'Feature Section',
    description: 'Describe main features and their benefits',
    element: aSectionElement('section_layout141'),
  }),
  aSection({
    name: 'Results Section',
    description: 'Highlight customer results',
    element: aSectionElement('section_layout218'),
  }),
  aSection({
    name: 'Features List Section',
    description: 'Describe other features and their benefits',
    element: aSectionElement('section_layout1'),
  }),
  aSection({
    name: 'Testimonial Section',
    description: 'Share customer testimonials',
    element: aSectionElement('section_testimonial1'),
  }),
  aSection({
    name: 'CTA Section',
    description: 'Invite visitor to try the product or service',
    element: aSectionElement('section_cta51'),
  }),
  aSection({
    name: 'FAQ Section',
    description: 'Answer frequently asked questions',
    element: aSectionElement('section_faq1'),
  }),
  aSection({
    name: 'Blog List Section',
    description: 'Share resources that create value for customers',
    element: aSectionElement('section_blog36'),
  }),
  aSection({
    name: 'Footer',
    element: aSectionElement('footer6_component'),
  }),
];

type Props = {
  project: Project;
  wireframeNavigation: WireframeNavigation;
  toastQueue: ToastQueue;
};

export const DemoPage = observer(
  ({ project, wireframeNavigation, toastQueue }: Props) => {
    const handleGoToWireframes = () => {
      editorModeStore.set(EditorMode.Wireframe);
      wireframeNavigation.openEditPanelIfClosed();
    };

    const handleStartWithDemo = () => {
      project.homePage.sections.push(
        ...DEMO_SECTIONS.map((section) => anInlineSection(section)),
      );

      toastQueue.push({
        confetti: true,
        theme: 'light',
        label: 'Demo concept created!',
      });
    };

    return (
      <div className={styles.page}>
        <div className={styles.verticalSpacer} />
        <div className={styles.container}>
          <div className={styles.spacer} />
          <div className={styles.leftPane}>
            <Title3>Welcome to Style Guide Builder</Title3>
            <TextRegularLight>
              It looks like you haven't got any pages set up in this project
              yet. Pick a path to get started
            </TextRegularLight>
            <div className={styles.footer}>
              <div className={styles.credits}>
                <div className={styles.lozenge}>
                  <TextTiny color="gradient">Beta testing V1.0</TextTiny>
                </div>
                <img src={DemoPageFacesImage} className={styles.faces} />
                <TextTinyLight color="grey">
                  Thank you for helping us test and release a better V1
                </TextTinyLight>
              </div>
            </div>
          </div>
          <div className={styles.rightPane}>
            {project.homePage.sections.length === 0 && (
              <div className={styles.section}>
                <div className={styles.column}>
                  <TextMedium>Start with a Demo</TextMedium>
                  <TextSmallLight>
                    Jump straight into the Style Guide Builder with a demo to
                    explore and experiment
                  </TextSmallLight>
                  <div className={styles.footer}>
                    <Button
                      variant="primary"
                      label="Get started"
                      onClick={handleStartWithDemo}
                    />
                  </div>
                </div>
                <img src={DemoPage1Image} className={styles.image} />
              </div>
            )}
            <div className={styles.section}>
              <div className={styles.column}>
                <TextMedium>Build a Homepage</TextMedium>
                <TextSmallLight>
                  Build a homepage in the Wireframe tab and return here to edit
                  the Style Guide
                </TextSmallLight>
                <div className={styles.footer}>
                  <Button
                    variant="secondary"
                    label="Go to Wireframes"
                    onClick={handleGoToWireframes}
                  />
                </div>
              </div>
              <img src={DemoPage2Image} className={styles.image} />
            </div>
          </div>
          <div className={styles.spacer} />
        </div>
        <div className={styles.verticalSpacer} />
      </div>
    );
  },
);
